import {useEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useMatch} from 'react-router'
import clsx from 'clsx'
import {useLayout} from '../../core'
import {DrawerComponent} from '../../../assets/ts/components'
import {WithChildren} from '../../../helpers'
import useAppToast from 'src/hooks/useAppToast'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import InitialSyncModal from 'src/utils/modals/InitialSyncAlert'
import InitialSyncProgressBanner, {
  InitialSyncCompletionSnapshot,
  InitialSyncProgressDetailSnapshot,
} from 'src/utils/modals/InitialSyncProgressBanner'
import {getInitialSyncStatusAsync} from 'src/redux/actions/authActions'
import ConnectChannelAlert from 'src/utils/modals/ConnectChannelAlert'
import {
  readInitialSyncCompletion,
  removeInitialSyncCompletion,
  saveInitialSyncCompletion,
} from 'src/utils/initialSyncCompletionStorage'

const Content = ({children}: WithChildren) => {
  const {config, classes} = useLayout()
  const location = useLocation()
  const {errorToast} = useAppToast()
  const dispatch = useAppDispatch()
  const initialSync = useAppSelector((state) => state.auth.initialSync)
  const initialPlatformSyncMessage = useAppSelector(
    (state) => state.auth.initialPlatformSyncMessage
  )
  const initialSyncProgressDetail = useAppSelector(
    (state) => state.auth.initialSyncProgressDetail
  )
  const initialSyncSteps = useAppSelector((state) => state.auth.initialSyncSteps)
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const {fetchedConnectedPlatformData, fetchedConnectedPlatformLoading} = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )
  const [showChannelAlert, setShowChannelAlert] = useState(false)
  const [completionSnapshot, setCompletionSnapshot] =
    useState<InitialSyncCompletionSnapshot | null>(null)
  const [completionSummaryDismissed, setCompletionSummaryDismissed] = useState(false)
  const prevInitialSyncRef = useRef<boolean | null>(null)
  const restoredCompletionScopeRef = useRef<string | null>(null)
  const inProgressCaptureRef = useRef<{
    platformId: number | null
    progressDetail: InitialSyncProgressDetailSnapshot[]
    productsFetchedCount: number | null
  }>({
    platformId: null,
    progressDetail: [],
    productsFetchedCount: null,
  })
  const isBillingPath = [
    '/pricing',
    '/pricing-plans',
    '/manage-pricing',
    '/billing-detail',
    '/transaction-summary',
    '/invoice',
  ].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
  const listingProductTableNewMatch = useMatch('/listing-product-table-new/:platformId/:id')
  const listingProductTableMatch = useMatch('/listing-product-table/:platformId/:id')
  const listingProductTableOldMatch = useMatch('/listing-product-table-old/:platformId/:id')
  const listingProductTableAgMatch = useMatch('/listing-product-table-ag/:platformId/:id')
  const platformSettingNewMatch = useMatch(
    '/platform-setting-new/:availablePlatformId/:selectedPlatformId'
  )
  const platformSettingBetaMatch = useMatch(
    '/platform-setting-beta/:availablePlatformId/:selectedPlatformId'
  )
  const channelSettingMatch = useMatch('/channel-settings/:availablePlatformId/:selectedPlatformId')
  const channelSettingLegacyMatch = useMatch(
    '/channel-settings-legacy/:availablePlatformId/:selectedPlatformId'
  )
  const platformSettingMatch = useMatch('/platform-setting/:selectedPlatformId')
  const isSecondaryStore = (p: any) =>
    !p?.isMainStore &&
    !p?.isAccountingStore &&
    !p?.isShippingStore &&
    !p?.isErpStore &&
    !p?.isOmsStore
  useEffect(() => {
    DrawerComponent.hideAll()
  }, [location])

  // Initial Sync Handling
  const resolvedChannelId = useMemo(() => {
    const ids = [
      listingProductTableNewMatch?.params?.id,
      listingProductTableMatch?.params?.id,
      listingProductTableOldMatch?.params?.id,
      listingProductTableAgMatch?.params?.id,
      platformSettingNewMatch?.params?.selectedPlatformId,
      platformSettingBetaMatch?.params?.selectedPlatformId,
      channelSettingMatch?.params?.selectedPlatformId,
      channelSettingLegacyMatch?.params?.selectedPlatformId,
      platformSettingMatch?.params?.selectedPlatformId,
    ]
    const id = ids.find(Boolean)
    return id ? Number(id) : null
  }, [
    listingProductTableNewMatch,
    listingProductTableMatch,
    listingProductTableOldMatch,
    listingProductTableAgMatch,
    platformSettingNewMatch,
    platformSettingBetaMatch,
    channelSettingMatch,
    channelSettingLegacyMatch,
    platformSettingMatch,
  ])

  const channelFromRoute = useMemo(
    () => fetchedConnectedPlatformData?.find((p: any) => p?.id === resolvedChannelId),
    [fetchedConnectedPlatformData, resolvedChannelId]
  )

  const syncTargetPlatform = useMemo(() => {
    if (channelFromRoute && isSecondaryStore(channelFromRoute)) {
      return channelFromRoute
    }
    return (
      fetchedConnectedPlatformData?.find((p: any) => p?.isMainStore) ??
      fetchedConnectedPlatformData?.find((p: any) => p?.isAccountingStore) ??
      fetchedConnectedPlatformData?.[0]
    )
  }, [channelFromRoute, fetchedConnectedPlatformData])

  const getPlatformDisplayName = (platform: any) =>
    platform?.display_name ||
    platform?.platform_info?.display_name ||
    platform?.platform_info?.name ||
    ''

  const getStoreIdentifier = (platform: any) =>
    platform?.friendlyName ||
    platform?.friendly_name ||
    platform?.credentials?.identifier ||
    platform?.store_name ||
    ''

  const activeSyncPlatformId = useMemo(() => {
    if (channelFromRoute && isSecondaryStore(channelFromRoute)) return channelFromRoute.id
    return syncTargetPlatform?.id ?? null
  }, [channelFromRoute, syncTargetPlatform])

  const fetchInitialSync = async () => {
    try {
      const params =
        channelFromRoute && isSecondaryStore(channelFromRoute)
          ? {platform_id: channelFromRoute.id}
          : {}

      const response = await dispatch(getInitialSyncStatusAsync(params))
      if (response?.meta?.requestStatus !== 'fulfilled') {
        errorToast(response?.payload?.data?.detail)
      }
    } catch (error: any) {
      errorToast(error?.message)
    }
  }

  useEffect(() => {
    // wait until connected platforms are known to decide whether to pass platform_id
    if (fetchedConnectedPlatformLoading) return

    fetchInitialSync()

    const intervalId = setInterval(fetchInitialSync, 30000) // Call Initial Sync Status every 30sec

    return () => clearInterval(intervalId)
  }, [fetchedConnectedPlatformLoading, channelFromRoute])

  useEffect(() => {
    if (
      !fetchedConnectedPlatformLoading &&
      fetchedConnectedPlatformData?.length === 0 &&
      fetchedUserAccountData &&
      fetchedUserAccountData?.name !== 'Superadmin' &&
      fetchedUserAccountData?.is_subscribed &&
      fetchedUserAccountData?.isOnboardingProcessCompleted
    ) {
      setShowChannelAlert(true)
    } else {
      setShowChannelAlert(false)
    }
  }, [fetchedConnectedPlatformData])

  useEffect(() => {
    const isInProgress = initialSync === false && initialPlatformSyncMessage !== ''
    if (!isInProgress) return

    const platformId =
      channelFromRoute && isSecondaryStore(channelFromRoute)
        ? channelFromRoute.id
        : syncTargetPlatform?.id ?? null

    inProgressCaptureRef.current = {
      platformId: platformId ?? null,
      progressDetail: initialSyncProgressDetail.map((item) => ({
        display_name: item.display_name,
        remaining_qty: item.remaining_qty,
        total_qty_fetched: item.total_qty_fetched,
        total_qty_available_in_channel: item.total_qty_available_in_channel,
      })),
      productsFetchedCount:
        typeof initialSyncSteps.productsFetched === 'number'
          ? initialSyncSteps.productsFetched
          : null,
    }
  }, [
    initialSync,
    initialPlatformSyncMessage,
    initialSyncProgressDetail,
    initialSyncSteps.productsFetched,
    channelFromRoute,
    syncTargetPlatform,
  ])

  useEffect(() => {
    const userId = fetchedUserAccountData?.id
    if (userId == null || activeSyncPlatformId == null) return

    const scope = `${userId}:${activeSyncPlatformId}`
    if (restoredCompletionScopeRef.current === scope) return

    const isInProgress = initialSync === false && initialPlatformSyncMessage !== ''
    if (isInProgress) return

    const record = readInitialSyncCompletion(userId, activeSyncPlatformId)
    if (!record) {
      if (completionSnapshot?.platformId === activeSyncPlatformId) {
        saveInitialSyncCompletion(userId, activeSyncPlatformId, {
          dismissed: completionSummaryDismissed,
          snapshot: completionSnapshot,
        })
        restoredCompletionScopeRef.current = scope
        return
      }
      if (
        completionSnapshot?.platformId != null &&
        completionSnapshot.platformId !== activeSyncPlatformId
      ) {
        setCompletionSnapshot(null)
        setCompletionSummaryDismissed(false)
      }
      return
    }

    restoredCompletionScopeRef.current = scope
    setCompletionSnapshot(record.snapshot)
    setCompletionSummaryDismissed(record.dismissed)
  }, [
    fetchedUserAccountData?.id,
    activeSyncPlatformId,
    initialSync,
    initialPlatformSyncMessage,
    completionSnapshot,
    completionSummaryDismissed,
  ])

  useEffect(() => {
    const prev = prevInitialSyncRef.current
    const isInProgress = initialSync === false && initialPlatformSyncMessage !== ''
    const isComplete = initialSync === true
    const userId = fetchedUserAccountData?.id

    if (isInProgress && prev === true) {
      if (userId != null && activeSyncPlatformId != null) {
        removeInitialSyncCompletion(userId, activeSyncPlatformId)
      }
      restoredCompletionScopeRef.current = null
      setCompletionSnapshot(null)
      setCompletionSummaryDismissed(false)
    }

    if (prev === false && isComplete) {
      const captured = inProgressCaptureRef.current
      const platform =
        captured.platformId != null
          ? fetchedConnectedPlatformData?.find((p: any) => p?.id === captured.platformId) ??
            syncTargetPlatform
          : syncTargetPlatform

      const snapshot: InitialSyncCompletionSnapshot = {
        platformId: captured.platformId ?? platform?.id ?? null,
        platformDisplayName: getPlatformDisplayName(platform),
        storeIdentifier: getStoreIdentifier(platform),
        progressDetail: captured.progressDetail,
        productsFetchedCount: captured.productsFetchedCount,
      }
      setCompletionSnapshot(snapshot)
      setCompletionSummaryDismissed(false)
      if (userId != null && snapshot.platformId != null) {
        saveInitialSyncCompletion(userId, snapshot.platformId, {
          dismissed: false,
          snapshot,
        })
        restoredCompletionScopeRef.current = `${userId}:${snapshot.platformId}`
      }
    }

    prevInitialSyncRef.current = initialSync
  }, [
    initialSync,
    initialPlatformSyncMessage,
    fetchedConnectedPlatformData,
    syncTargetPlatform,
    fetchedUserAccountData?.id,
    activeSyncPlatformId,
  ])

  const appContentContainer = config.app?.content?.container
  const isDisconnectSource = fetchedUserAccountData?.source === 'DISCONNECT'
  const isInitialSyncComplete = initialSync === true
  const isInitialSyncInProgress = initialSync === false && initialPlatformSyncMessage !== ''
  const showInitialSyncProgressBanner =
    !isBillingPath &&
    isDisconnectSource &&
    !isInitialSyncComplete &&
    isInitialSyncInProgress &&
    fetchedUserAccountData?.name !== 'Superadmin'
  const showInitialSyncModal =
    !isBillingPath &&
    !showInitialSyncProgressBanner &&
    isInitialSyncInProgress &&
    fetchedUserAccountData &&
    fetchedUserAccountData?.name !== 'Superadmin'
  const showInitialSyncCompletionSummary =
    !isBillingPath &&
    isDisconnectSource &&
    !completionSummaryDismissed &&
    completionSnapshot !== null &&
    fetchedUserAccountData?.name !== 'Superadmin'

  return (
    <div
      id='kt_app_content'
      className={clsx(
        'app-content flex-column-fluid',
        classes.content.join(' '),
        config?.app?.content?.class
      )}
    >
      {showInitialSyncModal && (
        <InitialSyncModal initialPlatformSyncMessage={initialPlatformSyncMessage} />
      )}
      {showInitialSyncProgressBanner && <InitialSyncProgressBanner />}
      {showInitialSyncCompletionSummary && completionSnapshot && (
        <InitialSyncProgressBanner
          variant='complete'
          snapshot={completionSnapshot}
          onDismiss={() => {
            setCompletionSummaryDismissed(true)
            const userId = fetchedUserAccountData?.id
            if (userId != null && completionSnapshot.platformId != null) {
              saveInitialSyncCompletion(userId, completionSnapshot.platformId, {
                dismissed: true,
                snapshot: completionSnapshot,
              })
            }
          }}
        />
      )}
      {/* Connect Channel alert when user has not connected any channel */}
      {showChannelAlert && <ConnectChannelAlert />}
      {appContentContainer ? (
        <div
          id='kt_app_content_container'
          className={clsx('app-container', classes.contentContainer.join(' '), {
            'container-xxl': appContentContainer === 'fixed',
            'container-fluid px-2 px-sm-5 mt-3': appContentContainer === 'fluid',
          })}
        >
          {children}
        </div>
      ) : (
        <>{children}</>
      )}
    </div>
  )
}

export {Content}
